import React, { useMemo } from 'react';
import { Tooltip } from 'react-tooltip';
import { ChartType } from '../../widgets/ProfitInsights';
import { convertValueToUSD } from '../../../../helpers/currency.helper';

export interface IDiffBarChartItem {
  income: number;
  expense: number;
  month: string;
}

interface IDiffBarChart {
  chartItems: IDiffBarChartItem[];
  chartType: number;
}

export const DiffBarChart = ({ chartItems, chartType }: IDiffBarChart) => {
  if (!chartItems) return null;
  const maxValue = useMemo(
    () => Math.max(...chartItems.map((item) => item.income), ...chartItems.map((item) => item.expense)),
    [chartItems],
  );
  const maxDivHeight = 125; // Maximum height of the divs in pixels
  const minDivHeight = 5;
  const barWidth = chartType === ChartType.BarChartDiff && chartItems?.length === 3 ? '10px' : '4px';

  const getHeight = (value: number) => {
    if (value === 0) {
      return { height: '4px', width: barWidth };
    }
    if (value > 0 && value < 10) {
      return { height: `${minDivHeight}px`, width: barWidth };
    }
    return { height: `${value}px`, width: barWidth };
  };

  return (
    <div className='' data-testid='DiffBarChart__wrapper'>
      <div className='flex flex-row items-end justify-evenly bg-bw00 rounded-lg h-[150px]'>
        {chartItems?.map((item) => {
          // Calculate the height of the income and expense divs as a proportion of the maximum value, multiplied by the maximum height
          const incomeHeight = maxValue !== 0 ? (item.income / maxValue) * maxDivHeight : 0;
          const expenseHeight = maxValue !== 0 ? (item.expense / maxValue) * maxDivHeight : 0;
          const profit = item.income - item.expense;
          return (
            <div
              data-testid={`DiffBarChart__${item.month}`}
              key={item.month}
              className='flex flex-col items-center w-[24px] py-[10px] hover:bg-bw02 rounded-[4px] select-month-diff-bar-btn'
              id={`diff-bar-tooltip-${item.month}`}
            >
              <Tooltip anchorSelect={`#diff-bar-tooltip-${item.month}`} place='top'>
                <ul className='text-left font-metricR text-[12px]'>
                  <li>
                    <span className='text-bw03'>Profit: </span>
                    <span
                      className={`${profit >= 0 ? 'text-badgeGreen03' : 'text-red02'}`}
                    >
                      {`${convertValueToUSD(profit, 2)}`}
                    </span>
                  </li>
                  <li>
                    <span className='text-bw03'>Income: </span>
                    <span className='text-bw00'>{`${convertValueToUSD(item.income, 2)}`}</span>
                  </li>
                  <li>
                    <span className='text-bw03'>Expenses: </span>
                    <span className='text-bw00'>{`${convertValueToUSD(item.expense, 2)}`}</span>
                  </li>
                </ul>
              </Tooltip>
              <div className='flex '>
                <div
                  id='income'
                  className={`self-end w-[4px] h-[100px] rounded-lg mr-[2px] ${incomeHeight === 0 ? 'bg-bw04' : 'bg-action02'}`}
                  style={getHeight(incomeHeight)}
                  data-testid={`DiffBarChart__income__${item.month}`}
                />
                <div
                  id='expense'
                  className={`self-end w-[4px] h-[100px] rounded-lg ${expenseHeight === 0 ? 'bg-bw04' : 'bg-action03'} `}
                  style={getHeight(expenseHeight)}
                  data-testid={`DiffBarChart__expense__${item.month}`}
                />
              </div>
              <div className='text-[11px] mt-[5px]' data-testid={`DiffBarChart__month-name__${item.month}`}>
                {item.month}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
